import { Table, TableBody, TableCell, TableHead, TableRow, Box, Typography } from "@mui/material";
import dayjs from "dayjs";

const dateOnlyPattern = /^\d{4}-\d{2}-\d{2}$/;
const dateTimePattern = /^\d{4}-\d{2}-\d{2}(?:[T\s]\d{2}:\d{2}(?::\d{2})?(?:\.\d{1,3})?(?:Z|[+-]\d{2}:?\d{2})?)?$/;
const dateOnlyFields = new Set([
	"planned_sample_date",
	"sample_ready_date",
	"expected_production_inward",
	"sample_inward_date",
]);
const istDateTimeFormatter = new Intl.DateTimeFormat("en-IN", {
	timeZone: "Asia/Kolkata",
	day: "2-digit",
	month: "short",
	year: "numeric",
	hour: "2-digit",
	minute: "2-digit",
	hour12: true,
});
const istDateFormatter = new Intl.DateTimeFormat("en-IN", {
	timeZone: "Asia/Kolkata",
	day: "2-digit",
	month: "short",
	year: "numeric",
});

const normalizeDateValue = (value) => (typeof value === "string" ? value.replace(" ", "T") : value);
const isDateLikeString = (val) =>
	typeof val === "string" && dateTimePattern.test(val) && dayjs(normalizeDateValue(val)).isValid();

const formatDateOnlyValue = (value) => {
	const date = new Date(normalizeDateValue(value));
	if (Number.isNaN(date.getTime())) return value;
	return istDateFormatter.format(date);
};

const formatTableValue = (value, fieldKey) => {
	if (dateOnlyFields.has(fieldKey) && isDateLikeString(value)) {
		return formatDateOnlyValue(value);
	}
	if (typeof value === "string" && dateOnlyPattern.test(value) && dayjs(value).isValid()) {
		return formatDateOnlyValue(`${value}T00:00:00`);
	}

	if (isDateLikeString(value)) {
		const date = new Date(normalizeDateValue(value));
		if (!Number.isNaN(date.getTime())) {
			return istDateTimeFormatter.format(date).replace(",", " •");
		}
	}

	return value;
};

const HistoryTable = ({ data, allowedKeys = [] }) => {
	const filteredData = (data || []).filter((row) =>
		Object.keys(row || {}).some(
			(k) =>
				!["id", "i_code", "createdAt", "updatedAt", "last_updated_by"].includes(k) &&
				(!allowedKeys.length || allowedKeys.includes(k)) &&
				row[k] !== null &&
				row[k] !== ""
		)
	);

	if (!filteredData.length)
		return (
			<Typography textAlign="center" p={2} color="gray">
				No updates found
			</Typography>
		);

	const sorted = [...filteredData].sort((a, b) => new Date(b.updatedAt || b.createdAt) - new Date(a.updatedAt || a.createdAt));

	return (
		<Box
			sx={{
				border: "1px solid #ddd",
				borderRadius: 2,
				overflow: "hidden",
				overflowY: "auto",
			}}>
			<Table
				size="small"
				stickyHeader
				sx={{
					tableLayout: "fixed",
					"& th, & td": {
						borderRight: "1px solid #eee",
						wordBreak: "break-word",
						whiteSpace: "normal",
						py: 1,
						px: 1,
					},
				}}>
				<TableHead>
					<TableRow>
						<TableCell sx={{ width: "22%" }}>
							<b>Date</b>
						</TableCell>
						<TableCell sx={{ width: "28%" }}>
							<b>Field</b>
						</TableCell>
						<TableCell sx={{ width: "30%" }}>
							<b>Value</b>
						</TableCell>
						<TableCell sx={{ width: "20%" }}>
							<b>Updated By</b>
						</TableCell>
					</TableRow>
				</TableHead>

				<TableBody>
					{sorted.map((row, i) => {
						const keys = Object.keys(row).filter(
							(k) =>
								!["id", "i_code", "createdAt", "updatedAt", "last_updated_by"].includes(k) &&
								(!allowedKeys.length || allowedKeys.includes(k)) &&
								row[k] !== null &&
								row[k] !== ""
						);

						return keys.map((k, index) => (
							<TableRow key={`${i}-${index}`}>
								{index === 0 ? (
									<TableCell rowSpan={keys.length} sx={{ fontWeight: 600 }}>
										{formatTableValue(row.updatedAt || row.createdAt)}
									</TableCell>
								) : null}

								<TableCell>{String(k).replace(/_/g, " ").toUpperCase()}</TableCell>
								<TableCell>{formatTableValue(row[k], k)}</TableCell>

								{index === 0 ? (
									<TableCell rowSpan={keys.length} sx={{ fontSize: 13 }}>
										{row.last_updated_by}
									</TableCell>
								) : null}
							</TableRow>
						));
					})}
				</TableBody>
			</Table>
		</Box>
	);
};

export default HistoryTable;
